import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useA11y } from '../context/AccessibilityContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { TourVideoButton, TourVideoDialog } from '../components/TourVideoDialog';
import { useAuth } from '../context/AuthContext';
import { trackTour } from '../lib/analytics';
import { blockStyle, CAT_LABEL } from '../lib/blockColors';
import { CalendarClock, Sparkles, RefreshCw, Plus, Timer, Play, Pause, RotateCcw, Trash2, CheckCircle2, Circle, Coffee, Loader2, CalendarSync, CalendarCheck, Bell, Repeat, Flag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CATS = {
  deep_work: { label: 'Deep work', bar: 'hsl(var(--brand-mint))', tint: 'hsla(165,55%,40%,0.12)' },
  meeting: { label: 'Meeting', bar: 'hsl(var(--warning))', tint: 'hsla(38,92%,52%,0.14)' },
  admin: { label: 'Admin', bar: 'hsl(var(--primary))', tint: 'hsla(18,92%,52%,0.12)' },
  break: { label: 'Break', bar: 'hsl(var(--muted-foreground))', tint: 'hsla(28,30%,90%,0.6)' },
  personal: { label: 'Personal', bar: 'hsl(var(--brand-coral))', tint: 'hsla(10,85%,58%,0.12)' },
};
const ROW_H = 46; // px per 30 min

const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const fmt = (m) => { let h = Math.floor(m / 60); const mm = m % 60; const ap = h >= 12 ? 'pm' : 'am'; h = h % 12 || 12; return `${h}:${String(mm).padStart(2, '0')}${ap}`; };
const toMin = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.frequency.value = 660; g.gain.value = 0.06;
    o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 220);
  } catch (e) {}
}

function FocusTimer({ onComplete, lowStim, request }) {
  const [minutes, setMinutes] = useState(25);
  const [remaining, setRemaining] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  const [banner, setBanner] = useState('');
  const [label, setLabel] = useState('');
  const alerted = useRef({});
  const total = minutes * 60;

  useEffect(() => { if (!running) setRemaining(minutes * 60); }, [minutes, running]);

  // External "prep for this meeting" trigger
  useEffect(() => {
    if (!request || !request.nonce) return;
    const mins = request.minutes || 15;
    setMinutes(mins);
    setRemaining(mins * 60);
    setLabel(request.label || '');
    alerted.current = {};
    setRunning(true);
  }, [request]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setRemaining((r) => {
        const nr = r - 1;
        [600, 300].forEach((mark) => {
          if (nr === mark && !alerted.current[mark]) {
            alerted.current[mark] = true;
            setBanner(`${mark / 60} minutes left — start wrapping up gently.`);
            if (!lowStim) beep();
            setTimeout(() => setBanner(''), 6000);
          }
        });
        if (nr <= 0) {
          clearInterval(t);
          setRunning(false);
          alerted.current = {};
          if (!lowStim) beep();
          onComplete(minutes);
          return 0;
        }
        return nr;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running, minutes, onComplete, lowStim]);

  const pct = total ? ((total - remaining) / total) * 100 : 0;
  const R = 52; const C = 2 * Math.PI * R;
  const mm = Math.floor(remaining / 60); const ss = remaining % 60;

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5">
      <div className="flex items-center gap-2 mb-3"><Timer className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} /><h3 className="font-display font-600">Focus timer</h3></div>
      {label && <div className="mb-3 text-xs rounded-lg px-3 py-2 border border-[hsl(var(--primary)/0.3)] flex items-center gap-1.5" style={{ background: 'hsl(var(--primary) / 0.08)', color: 'hsl(var(--primary))' }} data-testid="focus-timer-label"><CalendarClock className="h-3.5 w-3.5" /> Prepping: {label}</div>}
      {banner && <div className="mb-3 text-xs rounded-lg px-3 py-2 border border-[hsl(var(--warning)/0.4)]" style={{ background: 'hsl(var(--warning) / 0.14)' }} data-testid="focus-timer-banner">{banner}</div>}
      <div className="grid place-items-center py-2">
        <div className="relative h-32 w-32">
          <svg viewBox="0 0 120 120" className="h-32 w-32 -rotate-90">
            <circle cx="60" cy="60" r={R} fill="none" stroke="hsl(var(--secondary))" strokeWidth="10" />
            <circle cx="60" cy="60" r={R} fill="none" stroke="hsl(var(--primary))" strokeWidth="10" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C - (pct / 100) * C} style={{ transition: 'stroke-dashoffset 1s linear' }} />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <span className="font-mono text-2xl font-600" data-testid="focus-timer-remaining">{String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 mt-3">
        {[15, 25, 50].map((p) => (
          <button key={p} onClick={() => { setRunning(false); setMinutes(p); }} className={`text-xs px-2.5 py-1 rounded-lg border ${minutes === p ? 'bg-[hsl(var(--accent)/0.12)] border-[hsl(var(--accent)/0.3)] text-[hsl(var(--accent))]' : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]'}`} data-testid={`focus-preset-${p}`}>{p}m</button>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-4">
        {!running ? (
          <Button className="flex-1 rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={() => setRunning(true)} data-testid="focus-timer-start-button"><Play className="h-4 w-4 mr-1.5" /> Start</Button>
        ) : (
          <Button className="flex-1 rounded-xl" variant="secondary" onClick={() => setRunning(false)} data-testid="focus-timer-pause-button"><Pause className="h-4 w-4 mr-1.5" /> Pause</Button>
        )}
        <Button variant="secondary" size="icon" className="rounded-xl border border-[hsl(var(--border))]" onClick={() => { setRunning(false); setRemaining(minutes * 60); alerted.current = {}; }} data-testid="focus-timer-reset-button"><RotateCcw className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export default function TodayPage() {
  const { prefs } = useA11y();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [focusPick, setFocusPick] = useState(null);
  const [plan, setPlan] = useState({ blocks: [], capacity: { scheduled_min: 0, available_min: 540, work_start: 540, work_end: 1080 } });
  const [colors, setColors] = useState({ cal: {}, cat: {} });
  const [calNames, setCalNames] = useState({});
  const [isDark, setIsDark] = useState(() => (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')));
  useEffect(() => { api.get('/settings').then((r) => setColors(r.data?.block_colors || { cal: {}, cat: {} })).catch(() => {}); }, []);
  useEffect(() => {
    api.get('/calendar/calendars').then((r) => {
      const m = {};
      ['google', 'outlook'].forEach((p) => (r.data?.[p] || []).forEach((c) => { m[c.id] = c.name; }));
      setCalNames((prev) => ({ ...prev, ...m }));
    }).catch(() => {});
    api.get('/calendar/ics').then((r) => {
      const feeds = r.data?.feeds || r.data || [];
      const m = {};
      (Array.isArray(feeds) ? feeds : []).forEach((f) => { m[`ics:${f.feed_id}`] = f.name || 'Calendar link'; });
      setCalNames((prev) => ({ ...prev, ...m }));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const el = document.documentElement;
    const obs = new MutationObserver(() => setIsDark(el.classList.contains('dark')));
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  const [wins, setWins] = useState({ sessions: 0, minutes: 0, tasks_done: 0 });
  const [dueToday, setDueToday] = useState([]);
  const [dueDate, setDueDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, block: null });
  const [form, setForm] = useState({ title: '', category: 'deep_work', start: '09:00', end: '09:30' });
  const [calConnected, setCalConnected] = useState(false);
  const [reconnect, setReconnect] = useState(null);
  const [focusReq, setFocusReq] = useState(null);
  const [ready, setReady] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [dontShowTour, setDontShowTour] = useState(true);
  const tourTriggered = useRef(false);
  const tourWatched = useRef(false);
  const timerRef = useRef(null);
  const date = todayISO();

  const videoTourKey = `larkwell_video_tour_seen_${user?.user_id || 'anon'}`;
  const guidedTourKey = user?.user_id ? `larkwell_tour_done_${user.user_id}` : 'larkwell_tour_done';

  const startPrepFocus = (b) => {
    const mins = Math.min(25, Math.max(5, Math.round(((b.end - b.start) || 30) / 2)));
    setFocusReq({ label: b.title, minutes: mins, nonce: Date.now() });
    if (timerRef.current) timerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    toast(`Prep focus started for "${b.title}".`, { icon: '\uD83C\uDFAF' });
  };

  const load = useCallback(async () => {
    const [p, w, r] = await Promise.all([api.get(`/plan?date=${date}`), api.get('/focus/today'), api.get('/reminders/today')]);
    setPlan(p.data); setWins(w.data); setDueToday(r.data.tasks || []); setDueDate(r.data.date);
    setReady(true);
  }, [date]);
  useEffect(() => { load(); }, [load]);

  // Jev "Do-first" pick for the top-of-day focus banner (free triage).
  const loadFocus = useCallback(async () => {
    try { const res = await api.post('/tasks/triage'); setFocusPick((res.data?.top3 || [])[0] || null); }
    catch (e) { /* non-blocking */ }
  }, []);
  useEffect(() => { loadFocus(); }, [loadFocus]);

  // First-run onboarding: after the interactive guided tour is done, if Today is
  // still empty and they haven't seen the video tour, gently autoplay it once.
  const maybeOpenTour = useCallback(() => {
    if (tourTriggered.current) return;
    try {
      const seen = localStorage.getItem(videoTourKey) === '1';
      const guidedDone = localStorage.getItem(guidedTourKey) === '1';
      if (!seen && guidedDone && ready && plan.blocks.length === 0) {
        tourTriggered.current = true;
        setTimeout(() => { setTourOpen(true); trackTour('video', 'shown', 'autoplay'); }, 600);
      }
    } catch (e) { /* localStorage unavailable */ }
  }, [ready, plan.blocks.length, videoTourKey, guidedTourKey]);

  useEffect(() => { maybeOpenTour(); }, [maybeOpenTour]);
  useEffect(() => {
    const onFinished = () => maybeOpenTour();
    window.addEventListener('larkwell:tour-finished', onFinished);
    return () => window.removeEventListener('larkwell:tour-finished', onFinished);
  }, [maybeOpenTour]);

  const handleTourOpenChange = (open) => {
    if (!open) {
      if (dontShowTour) {
        try { localStorage.setItem(videoTourKey, '1'); } catch (e) { /* ignore */ }
      }
      if (!tourWatched.current) trackTour('video', 'skipped', 'autoplay');
      tourWatched.current = false;
    }
    setTourOpen(open);
  };

  const handleTourWatched = () => {
    tourWatched.current = true;
    trackTour('video', 'watched', 'autoplay');
  };

  // Pull the connected calendar (read-only) and show it on the timeline
  const pullCalendar = useCallback(async () => {
    try {
      const res = await api.post('/calendar/sync', { date, pull_only: true, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      if (res.data?.blocks) setPlan({ blocks: res.data.blocks, capacity: res.data.capacity });
      if (res.data?.reconnect_needed?.length) setReconnect(res.data.reconnect_needed[0]); else setReconnect(null);
    } catch (e) { console.debug('calendar auto-pull skipped', e); }
  }, [date]);

  const addDays = (iso, n) => {
    const parts = (iso || new Date().toISOString().slice(0, 10)).split('-').map(Number);
    const dt = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    dt.setUTCDate(dt.getUTCDate() + n);
    return dt.toISOString().slice(0, 10);
  };

  const snoozeDue = async (t) => {
    const tomorrow = addDays(dueDate, 1);
    try {
      await api.put(`/tasks/${t.task_id}`, { title: t.title, due_date: tomorrow });
      setDueToday((list) => list.filter((x) => x.task_id !== t.task_id));
      toast('Snoozed to tomorrow \u2014 no worries.', { icon: '\uD83D\uDE0C' });
    } catch (e) { toast.error('Could not snooze'); }
  };

  const completeDue = async (t) => {
    try {
      await api.post(`/tasks/${t.task_id}/complete`);
      setDueToday((list) => list.filter((x) => x.task_id !== t.task_id));
      toast.success('Done — nice. One less thing today.');
      api.get('/focus/today').then((w) => setWins(w.data)).catch(() => {});
    } catch (e) { toast.error('Could not complete'); }
  };

  // Auto-show the user's connected calendar, and keep it fresh automatically
  // (on load, when you return to the tab, and every few minutes) — no manual sync needed.
  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};
    (async () => {
      try {
        const s = await api.get('/calendar/status');
        const conns = s.data?.connections || {};
        const connected = Object.values(conns).some((c) => c?.connected);
        const icsCount = s.data?.ics?.count || 0;
        const hasSource = connected || icsCount > 0;
        if (cancelled) return;
        setCalConnected(connected);
        const rc = Object.entries(conns).find(([, c]) => c?.needs_reconnect);
        if (rc) {
          // A connected provider expired — surface the reconnect banner.
          setReconnect(rc[0]);
          // ICS feeds still work, so keep pulling those if present.
          if (icsCount === 0) return;
        } else {
          setReconnect(null);
        }
        if (!hasSource) return;
        await pullCalendar();
        const onVis = () => { if (document.visibilityState === 'visible') pullCalendar(); };
        document.addEventListener('visibilitychange', onVis);
        const iv = setInterval(() => { if (document.visibilityState === 'visible') pullCalendar(); }, 5 * 60 * 1000);
        cleanup = () => { document.removeEventListener('visibilitychange', onVis); clearInterval(iv); };
      } catch (e) { console.debug('calendar status skipped', e); }
    })();
    return () => { cancelled = true; cleanup(); };
  }, [pullCalendar]);

  const aiPlan = async () => {
    setLoading(true);
    try { const res = await api.post('/plan/ai', { date, respect_now: true }); setPlan(res.data); toast.success(res.data.created ? `Sketched ${res.data.created} blocks — pick one to start.` : 'No open tasks to plan. Add a few first!'); }
    catch (e) { toast.error('Could not plan your day'); } finally { setLoading(false); }
  };
  const replan = async () => {
    setLoading(true);
    try { const res = await api.post('/plan/replan', { date }); setPlan(res.data); toast.success('Replanned — unfinished blocks moved to later today.'); }
    catch (e) { toast.error('Could not replan'); } finally { setLoading(false); }
  };
  const syncCal = async () => {
    setLoading(true);
    try { const res = await api.post('/calendar/sync', { date, tz: Intl.DateTimeFormat().resolvedOptions().timeZone }); setPlan({ blocks: res.data.blocks, capacity: res.data.capacity }); if (res.data.reconnect_needed?.length) { setReconnect(res.data.reconnect_needed[0]); toast.error(res.data.message); } else { setReconnect(null); toast.success(res.data.message || `Synced — ${res.data.pulled} pulled.`); } }
    catch (e) { toast.error(e?.response?.data?.detail || 'Connect a calendar in Settings first.'); } finally { setLoading(false); }
  };

  const openNew = () => { setForm({ title: '', category: 'deep_work', start: '09:00', end: '10:00' }); setDialog({ open: true, block: null }); };
  const openEdit = (b) => { setForm({ title: b.title, category: b.category, start: toHHMM(b.start), end: toHHMM(b.end) }); setDialog({ open: true, block: b }); };
  const saveBlock = async () => {
    const start = toMin(form.start); const end = toMin(form.end);
    if (end <= start) { toast.error('End time must be after start'); return; }
    const payload = { date, title: form.title || CATS[form.category].label, category: form.category, start, end };
    try {
      if (dialog.block) await api.put(`/plan/blocks/${dialog.block.block_id}`, payload);
      else await api.post('/plan/blocks', payload);
      setDialog({ open: false, block: null }); load();
    } catch (e) { toast.error('Could not save block'); }
  };
  const delBlock = async (id) => { await api.delete(`/plan/blocks/${id}`); setDialog({ open: false, block: null }); load(); };

  const onFocusComplete = async (mins) => {
    try { await api.post('/focus', { minutes: mins, completed: true }); } catch (e) {}
    toast.success('Nice — that focus sprint is in the bank. Take a breath. \u2615');
    load();
  };

  const cap = plan.capacity;
  const capPct = cap.available_min ? Math.round((cap.scheduled_min / cap.available_min) * 100) : 0;
  const capState = capPct > 100 ? 'over' : capPct >= 80 ? 'tight' : 'ok';
  const capColor = capState === 'over' ? 'hsl(var(--destructive))' : capState === 'tight' ? 'hsl(var(--warning))' : 'hsl(var(--primary))';
  // Expand the visible window so events that fall before work-start or after
  // work-end still render inside the timeline (never bleeding over the header).
  const _blkMin = plan.blocks.length ? Math.min(...plan.blocks.map((b) => b.start)) : cap.work_start;
  const _blkMax = plan.blocks.length ? Math.max(...plan.blocks.map((b) => b.end)) : cap.work_end;
  const ws = Math.floor(Math.min(cap.work_start, _blkMin) / 30) * 30;
  const we = Math.ceil(Math.max(cap.work_end, _blkMax) / 30) * 30;
  const timelineH = ((we - ws) / 30) * ROW_H;
  const rows = []; for (let m = ws; m < we; m += 30) rows.push(m);

  // Side-by-side layout for overlapping blocks (calendar-style columns).
  // Groups transitively-overlapping blocks into clusters, then packs each
  // cluster into the fewest columns so nothing is hidden behind another block.
  const blockLayout = (() => {
    const map = {};
    const sorted = [...plan.blocks].sort((a, b) => (a.start - b.start) || (a.end - b.end));
    let cluster = [];
    let clusterEnd = -Infinity;
    const flush = () => {
      if (!cluster.length) return;
      const cols = []; // each col = end time of last block placed in it
      cluster.forEach((blk) => {
        let placed = false;
        for (let i = 0; i < cols.length; i++) {
          if (blk.start >= cols[i]) { cols[i] = blk.end; map[blk.block_id] = { col: i }; placed = true; break; }
        }
        if (!placed) { map[blk.block_id] = { col: cols.length }; cols.push(blk.end); }
      });
      const total = cols.length;
      cluster.forEach((blk) => { map[blk.block_id].cols = total; });
      cluster = []; clusterEnd = -Infinity;
    };
    sorted.forEach((blk) => {
      if (cluster.length && blk.start >= clusterEnd) flush();
      cluster.push(blk);
      clusterEnd = Math.max(clusterEnd, blk.end);
    });
    flush();
    return map;
  })();

  return (
    <div className="h-full flex flex-col min-h-0">
      <TourVideoDialog
        open={tourOpen}
        onOpenChange={handleTourOpenChange}
        autoplay
        dontShow={dontShowTour}
        onToggleDontShow={(v) => setDontShowTour(!!v)}
        onWatched={handleTourWatched}
      />
      <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-4 border-b border-[hsl(var(--border))]">
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-xl font-700 flex items-center gap-2"><CalendarClock className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /> Today</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })} — one gentle step at a time.</p>
        </div>
        <Button variant="secondary" className="rounded-xl border border-[hsl(var(--border))] gap-1.5" onClick={syncCal} disabled={loading} data-testid="today-sync-calendar-button"><CalendarSync className="h-4 w-4" /> Sync calendar</Button>
        <Button variant="secondary" className="rounded-xl border border-[hsl(var(--border))] gap-1.5" onClick={replan} disabled={loading} data-testid="today-replan-button"><RefreshCw className="h-4 w-4" /> Replan</Button>
        <Button className="rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} onClick={aiPlan} disabled={loading} data-testid="today-ai-plan-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Plan my day</Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 sm:p-6 pb-0"><OnboardingChecklist /></div>
        {focusPick && (
          <div className="px-4 sm:px-6 pt-4">
            <div className="rounded-2xl border p-4 flex items-center gap-3" style={{ borderColor: 'hsl(var(--brand-coral) / 0.4)', background: 'hsl(var(--brand-coral) / 0.07)' }} data-testid="today-focus-banner">
              <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'hsl(var(--brand-coral) / 0.15)' }}><Flag className="h-5 w-5" style={{ color: 'hsl(var(--brand-coral))' }} /></div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-700 uppercase tracking-wide" style={{ color: 'hsl(var(--brand-coral))' }}>Start here today</p>
                <p className="font-600 truncate" data-testid="today-focus-title">{focusPick.title}{focusPick.is_sticky && <span className="ml-2 text-[11px] font-500 text-[hsl(var(--muted-foreground))]">\u00b7 an ugh task \u2014 start impossibly small</span>}</p>
              </div>
              <Button size="sm" className="rounded-xl gap-1.5 text-white shrink-0" style={{ background: 'hsl(var(--brand-coral))' }} onClick={() => navigate('/app/tasks')} data-testid="today-focus-open">Open</Button>
            </div>
          </div>
        )}
        <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* Timeline */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-4">
            <div className="relative z-20 flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-display font-600">Your timeline</h3>
                {calConnected && !reconnect && <Badge variant="secondary" className="text-[10px] gap-1" data-testid="calendar-connected-badge"><CalendarCheck className="h-3 w-3" style={{ color: 'hsl(var(--success))' }} /> Google Calendar connected</Badge>}
                {reconnect && <a href="/app/settings" className="text-[11px] rounded-lg px-2 py-1 inline-flex items-center gap-1 border" style={{ borderColor: 'hsl(var(--destructive)/0.4)', color: 'hsl(var(--destructive))', background: 'hsl(var(--destructive)/0.06)' }} data-testid="calendar-reconnect-banner"><CalendarSync className="h-3 w-3" /> Your {reconnect} connection expired — reconnect</a>}
              </div>
              <Button size="sm" variant="ghost" className="rounded-xl gap-1.5" onClick={openNew} data-testid="today-add-block-button"><Plus className="h-4 w-4" /> Add block</Button>
            </div>
            {plan.blocks.length > 0 && (() => {
              const seen = new Set();
              const legend = [];
              for (const b of plan.blocks) {
                const key = b.calendar_id || `cat:${b.category || 'admin'}`;
                if (seen.has(key)) continue;
                seen.add(key);
                let label;
                if (b.calendar_id) label = calNames[b.calendar_id] || (String(b.calendar_id).startsWith('ics:') ? 'Calendar link' : (b.source === 'google' ? 'Google Calendar' : b.source === 'outlook' ? 'Outlook' : 'Calendar'));
                else label = CAT_LABEL[b.category] || 'Block';
                legend.push({ key, color: blockStyle(b, colors, isDark).color, label });
              }
              return (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-1 pb-2" data-testid="today-legend">
                  {legend.map((l) => (
                    <span key={l.key} className="inline-flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]" data-testid={`legend-${l.key}`}>
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: l.color }} />
                      {l.label}
                    </span>
                  ))}
                </div>
              );
            })()}
            {plan.blocks.length === 0 && (
              <div className="text-center py-10 px-6">
                <div className="h-12 w-12 rounded-2xl mx-auto grid place-items-center mb-3" style={{ background: 'hsl(var(--accent) / 0.12)' }}><Sparkles className="h-6 w-6" style={{ color: 'hsl(var(--accent))' }} /></div>
                <p className="font-600">No plan yet</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Want Larkwell to sketch a gentle day from your tasks?</p>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                  <Button className="rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={aiPlan} disabled={loading} data-testid="today-empty-plan-button">Plan my day</Button>
                  <TourVideoButton label="Watch the 2-min tour" variant="secondary" testId="today-watch-tour-button" />
                </div>
              </div>
            )}
            {plan.blocks.length > 0 && (
              <div className="overflow-y-auto overflow-x-hidden -mr-2 pr-2" style={{ maxHeight: 'min(640px, 62vh)' }} data-testid="today-timeline-scroll">
              <div className="relative" style={{ height: timelineH }}>
                {/* hour rows */}
                {rows.map((m) => (
                  <div key={m} className="absolute left-0 right-0 flex" style={{ top: ((m - ws) / 30) * ROW_H, height: ROW_H }}>
                    <div className="w-14 shrink-0 text-xs text-[hsl(var(--muted-foreground))] font-500 -mt-1.5">{m % 60 === 0 ? fmt(m) : ''}</div>
                    <div className="flex-1 border-t border-[hsl(var(--border)/0.7)]" />
                  </div>
                ))}
                {/* blocks */}
                <div className="absolute left-14 right-0 top-0 bottom-0">
                  {plan.blocks.map((b) => {
                    const c = CATS[b.category] || CATS.admin;
                    const st = blockStyle(b, colors, isDark);
                    const top = ((b.start - ws) / 30) * ROW_H;
                    const h = Math.max(((b.end - b.start) / 30) * ROW_H - 4, 24);
                    const isMeeting = b.source === 'google' || b.source === 'outlook' || b.category === 'meeting';
                    const lay = blockLayout[b.block_id] || { col: 0, cols: 1 };
                    const colW = 100 / lay.cols;
                    const isNarrow = lay.cols > 1;
                    return (
                      <motion.div key={b.block_id} initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} onClick={() => openEdit(b)}
                        role="button" tabIndex={0}
                        className="absolute rounded-xl border border-[hsl(var(--border))] shadow-sm px-3 py-1.5 text-left overflow-hidden hover:shadow-md hover:z-10 cursor-pointer group"
                        style={{ top, height: h, background: st.tint, left: `calc(${lay.col * colW}% + 4px)`, width: `calc(${colW}% - 8px)` }} data-testid={`block-${b.block_id}`}>
                        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl" style={{ background: st.bar }} />
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-600 truncate pl-1.5">{b.title}</p>
                            <p className="text-[11px] text-[hsl(var(--muted-foreground))] pl-1.5 truncate">{fmt(b.start)}–{fmt(b.end)}{!isNarrow && <> · {b.source === 'google' ? 'Google Calendar' : b.source === 'outlook' ? 'Outlook' : (CAT_LABEL[b.category] || c.label)}</>}</p>
                          </div>
                          {isMeeting && !isNarrow && (
                            <button
                              onClick={(e) => { e.stopPropagation(); startPrepFocus(b); }}
                              title="Start a focus timer to prep for this"
                              className="shrink-0 flex items-center gap-1 text-[11px] rounded-lg px-2 py-1 border border-[hsl(var(--border))] bg-card text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary)/0.4)] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                              data-testid={`block-prep-focus-${b.block_id}`}>
                              <Play className="h-3 w-3" /> Prep
                            </button>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
              </div>
            )}
          </div>

          {/* Right rail */}
          <div className="space-y-4">
            {dueToday.length > 0 && (
              <div className="rounded-2xl border p-5" style={{ borderColor: 'hsl(var(--primary) / 0.35)', background: 'hsl(var(--primary) / 0.05)' }} data-testid="due-today-card">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
                  <h3 className="font-display font-600">Due today</h3>
                  <Badge variant="secondary" className="text-[10px] ml-auto">{dueToday.length}</Badge>
                </div>
                <div className="space-y-1.5">
                  {dueToday.map((t) => (
                    <div key={t.task_id} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-card px-3 py-2" data-testid={`due-today-${t.task_id}`}>
                      <button onClick={() => completeDue(t)} data-testid={`due-today-complete-${t.task_id}`}>
                        <Circle className="h-4 w-4 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--success))]" />
                      </button>
                      <span className="text-sm flex-1 truncate">{t.title}</span>
                      {t.recurring && <Repeat className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary))' }} aria-label="Repeats" />}
                      <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{t.estimate_min}m</span>
                      <button onClick={() => snoozeDue(t)} title="Snooze to tomorrow" className="flex items-center gap-1 text-[11px] rounded-md px-1.5 py-0.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))]" data-testid={`due-today-snooze-${t.task_id}`}>
                        <CalendarClock className="h-3.5 w-3.5" /> Tomorrow
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-600">Capacity</h3>
                <Badge variant="secondary" className="text-[10px]" data-testid="capacity-badge">{capState === 'over' ? 'Overbooked' : capState === 'tight' ? 'Getting full' : 'Room to breathe'}</Badge>
              </div>
              <div className="h-2.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(capPct, 100)}%`, background: capColor, transition: 'width 0.4s ease' }} />
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2" data-testid="capacity-text">
                {(cap.scheduled_min / 60).toFixed(1)}h scheduled / {(cap.available_min / 60).toFixed(1)}h available
              </p>
            </div>

            <div ref={timerRef}>
              <FocusTimer onComplete={onFocusComplete} lowStim={prefs.low_stim} request={focusReq} />
            </div>

            <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5">
              <h3 className="font-display font-600 mb-3">Today's wins</h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[['Focus', wins.sessions, Timer], ['Minutes', wins.minutes, Coffee], ['Done', wins.tasks_done, CheckCircle2]].map(([label, val, Icon]) => (
                  <div key={label} className="rounded-xl bg-[hsl(var(--secondary)/0.6)] py-3">
                    <Icon className="h-4 w-4 mx-auto mb-1" style={{ color: 'hsl(var(--success))' }} />
                    <p className="font-display text-lg font-700">{val}</p>
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{dialog.block ? 'Edit block' : 'Add a time block'}</DialogTitle><DialogDescription>Small blocks beat big vague ones.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What is this block?" className="h-10 rounded-xl" data-testid="block-title-input" />
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="block-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[hsl(var(--muted-foreground))]">Start</label><Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="h-10 rounded-xl" data-testid="block-start-input" /></div>
              <div><label className="text-xs text-[hsl(var(--muted-foreground))]">End</label><Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="h-10 rounded-xl" data-testid="block-end-input" /></div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            {dialog.block && <Button variant="secondary" className="rounded-xl text-[hsl(var(--destructive))] mr-auto" onClick={() => delBlock(dialog.block.block_id)} data-testid="delete-block-button"><Trash2 className="h-4 w-4 mr-1.5" /> Delete</Button>}
            <Button onClick={saveBlock} className="rounded-xl" style={{ background: 'hsl(var(--primary))' }} data-testid="save-block-button">Save block</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
